import { useAppDispatch } from "@/app/hooks";
import {
  createUser,
  getUserDetails,
  resetUserPassword,
  updateUser,
} from "@/features";
import { UpdateUserFormType } from "@/types";
import { toastify } from "@/utils";
import { useFormik } from "formik";
import { useCallback, useEffect } from "react";
import Flatpickr from "react-flatpickr";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

type Props = {
  isOpen: boolean;
  selectedId: number;
  onSuccess: () => void;
  toggle: () => void;
};

export const CreateOrEditUser = ({
  isOpen,
  selectedId,
  onSuccess,
  toggle,
}: Props) => {
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
      subscription_status: "",
      subscription_expire_date: undefined,
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("Invalid email")
        .required("Please fill out this field."),
      password: selectedId
        ? Yup.string().notRequired()
        : Yup.string().required("Please fill out this field."),
      password_confirmation: selectedId
        ? Yup.string().notRequired()
        : Yup.string()
            .required("Please fill out this field.")
            .oneOf([Yup.ref("password")], "Passwords do not match."),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const formatValues: UpdateUserFormType = {
        ...values,
        id: selectedId,
      };

      try {
        if (selectedId) {
          await dispatch(updateUser(formatValues)).unwrap();
          toastify("User has been updated successfully", "success");
        } else {
          await dispatch(createUser(formatValues)).unwrap();
          toastify("User has been created successfully", "success");
        }

        onSuccess();
      } catch (error) {
        console.log("error: ", error);
        toastify("An error occurred. Please try again.", "error");
      } finally {
        toggle();
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const getUserDetailsInfo = useCallback(
    async (id: number) => {
      try {
        const details = await dispatch(getUserDetails(id)).unwrap();

        setFieldValue("first_name", details.first_name);
        setFieldValue("last_name", details.last_name);
        setFieldValue("email", details.email);
        setFieldValue(
          "subscription_status",
          details.subscription?.status_name ?? "",
        );
        setFieldValue(
          "subscription_expire_date",
          details.subscription?.expose_expire_date,
        );
        setFieldValue("password", "");
        setFieldValue("password_confirmation", "");
      } catch (error: any) {
        console.log("error: ", error);
      }
    },
    [dispatch, setFieldValue],
  );

  const onResetPassword = async () => {
    if (selectedId) {
      try {
        await dispatch(resetUserPassword(selectedId)).unwrap();
        toastify("Email has been sent to user successfully", "success");
      } catch (error) {
        console.log("error: ", error);
        toastify("Email sent unsuccessfully", "error");
      }
    }
  };

  useEffect(() => {
    if (selectedId) {
      getUserDetailsInfo(selectedId);
    }
  }, [getUserDetailsInfo, selectedId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          {selectedId ? "Edit User" : "Create User"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col xs={12} lg={6}>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  name="first_name"
                  placeholder="First Name"
                  value={values.first_name}
                  onChange={handleChange}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={6}>
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  name="last_name"
                  placeholder="Last Name"
                  value={values.last_name}
                  onChange={handleChange}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={6}>
              <FormGroup>
                <Label className="required">Email</Label>
                <Input
                  name="email"
                  placeholder="Email"
                  value={values.email}
                  onChange={handleChange}
                  className={errors.email && touched.email ? "is-invalid" : ""}
                />
                {errors.email && touched.email && (
                  <p className="text-danger mt-2">{errors.email}</p>
                )}
              </FormGroup>
            </Col>
            {!selectedId && (
              <>
                <Col xs={12} lg={6}>
                  <FormGroup>
                    <Label className="required">Password</Label>
                    <Input
                      type="password"
                      name="password"
                      placeholder="Password"
                      value={values.password}
                      onChange={handleChange}
                      className={
                        errors.password && touched.password ? "is-invalid" : ""
                      }
                    />
                    {errors.password && touched.password && (
                      <p className="text-danger mt-2">{errors.password}</p>
                    )}
                  </FormGroup>
                </Col>
                <Col xs={12} lg={6}>
                  <FormGroup>
                    <Label className="required">Confirmed Password</Label>
                    <Input
                      type="password"
                      name="password_confirmation"
                      placeholder="Confirmed Password"
                      value={values.password_confirmation}
                      onChange={handleChange}
                      className={
                        errors.password_confirmation &&
                        touched.password_confirmation
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {errors.password_confirmation &&
                      touched.password_confirmation && (
                        <p className="text-danger mt-2">
                          {errors.password_confirmation}
                        </p>
                      )}
                  </FormGroup>
                </Col>
              </>
            )}
            {selectedId && (
              <>
                <Col xs={12} lg={6}>
                  <FormGroup>
                    <Label>Trial Month</Label>
                    <Input
                    // name="password_confirmation"
                    // placeholder="Confirmed Password"
                    // value={values.password_confirmation}
                    // onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label>Expired Date</Label>
                    <Flatpickr
                      name="password_confirmation"
                      className="form-control"
                      options={{
                        dateFormat: "d/m/Y",
                      }}
                      placeholder="Users was created from"
                      value={values.subscription_expire_date ?? undefined}
                      onChange={(_, date) =>
                        setFieldValue("subscription_expire_date", date)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6} className="d-flex align-items-center mt-2">
                  <Button onClick={onResetPassword}>Reset Password</Button>
                </Col>
              </>
            )}
            {selectedId && (
              <Col xs={12} md={12}>
                <FormGroup>
                  <Label>Subcription Status</Label>
                  <FormGroup>
                    <Label className="me-4">
                      <Input
                        name="radio-status"
                        type="radio"
                        className="me-2"
                        checked={
                          values.subscription_status?.toLowerCase() === "active"
                        }
                        onChange={() =>
                          setFieldValue("subscription_status", "active")
                        }
                      />
                      Active
                    </Label>
                    <Label className="me-4">
                      <Input
                        name="radio-status"
                        type="radio"
                        className="me-2"
                        checked={
                          values.subscription_status?.toLowerCase() ===
                          "expired"
                        }
                        onChange={() =>
                          setFieldValue("subscription_status", "expired")
                        }
                      />
                      Expired
                    </Label>
                    <Label className="me-4">
                      <Input
                        name="radio-status"
                        type="radio"
                        className="me-2"
                        checked={
                          values.subscription_status?.toLowerCase() ===
                          "terminated"
                        }
                        onChange={() =>
                          setFieldValue("subscription_status", "terminated")
                        }
                      />
                      Terminated
                    </Label>
                    <Label className="me-4">
                      <Input
                        name="radio-status"
                        type="radio"
                        className="me-2"
                        checked={
                          values.subscription_status?.toLowerCase() === "revoke"
                        }
                        onChange={() =>
                          setFieldValue("subscription_status", "revoke")
                        }
                      />
                      Revoke
                    </Label>
                  </FormGroup>
                </FormGroup>
              </Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Close
          </Button>
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {selectedId ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
